/**
 * Analytics & SEO Helpers
 * Handles Google Analytics 4 datalayer events and Schema.org structured data
 */

/**
 * Push GA4 ecommerce event to dataLayer
 * @param {Object} datalayer - The datalayer object from API response
 */
export function pushDataLayerEvent(datalayer) {
	if (!datalayer || typeof datalayer !== 'object') {
		return;
	}

	// Ensure dataLayer exists
	window.dataLayer = window.dataLayer || [];

	// Push the event
	window.dataLayer.push(datalayer);
}

/**
 * Inject structured data (JSON-LD) into the page
 * @param {Array} structuredData - Array of schema.org objects from API response
 * @param {string} containerId - Optional ID for the container (default: auto-generated)
 */
export function injectStructuredData(structuredData, containerId = null) {
	if (!Array.isArray(structuredData) || structuredData.length === 0) {
		return;
	}

	// Generate or use provided container ID
	const id = containerId || `structured-data-${ Date.now() }-${ Math.random().toString(36).substring(2, 9) }`;

	// Remove existing container with same ID if it exists
	const existing = document.getElementById(id);
	if (existing) {
		existing.remove();
	}

	// Create script element for each schema object
	const container = document.createElement('div');
	container.id = id;
	container.className = 'structured-data-container';
	container.style.display = 'none';

	structuredData.forEach((schema, index) => {
		if (!schema || typeof schema !== 'object') {
			return;
		}

		const script = document.createElement('script');
		script.type = 'application/ld+json';
		script.textContent = JSON.stringify(schema);
		script.dataset.schemaIndex = String(index);
		container.appendChild(script);
	});

	// Append to document head
	document.head.appendChild(container);
}

/**
 * Process API response and handle both datalayer and structured data
 * @param {Object} response - API response object
 * @param {Object} options - Optional configuration
 * @param {boolean} options.clearPrevious - Whether to clear previous structured data (default: false)
 * @param {string} options.containerId - Custom container ID for structured data
 */
export function processShowListingResponse(response, options = {}) {
	if (!response || typeof response !== 'object') {
		return;
	}

	const { clearPrevious = false, containerId = null } = options;

	// Clear previous structured data if requested
	if (clearPrevious) {
		document.querySelectorAll('.structured-data-container').forEach((el) => el.remove());
	}

	// Handle datalayer event
	if (response.datalayer) {
		pushDataLayerEvent(response.datalayer);
	}

	// Handle structured data
	if (response.structured_data) {
		injectStructuredData(response.structured_data, containerId);
	}
}

/**
 * Clear all dynamically injected structured data
 */
export function clearStructuredData() {
	document.querySelectorAll('.structured-data-container').forEach((el) => el.remove());
}
